
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, Code, Wallet, GitBranch, Users, Brain } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const WhatWeOfferSection: React.FC = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { t } = useLanguage();
  
  const offerings = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: t('sales_title'),
      description: t('sales_desc'),
      color: "from-blue-600 to-blue-400"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: t('engineering_title'),
      description: t('engineering_desc'),
      color: "from-green-600 to-green-400"
    },
    {
      icon: <Wallet className="w-6 h-6" />,
      title: t('financial_title'),
      description: t('financial_desc'),
      color: "from-amber-600 to-amber-400"
    },
    {
      icon: <GitBranch className="w-6 h-6" />,
      title: t('partnership_title'),
      description: t('partnership_desc'),
      color: "from-purple-600 to-purple-400"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t('community_title'),
      description: t('community_desc'),
      color: "from-indigo-600 to-indigo-400"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: t('advisory_title'),
      description: t('advisory_desc'),
      color: "from-rose-600 to-rose-400"
    }
  ];
  
  return (
    <section id="what-we-offer" className="py-52 bg-gradient-to-br from-noa-rich-black to-noa-charcoal text-white relative">
      {/* Background grid pattern */}
      <div className="absolute inset-0 bg-tech-grid-pattern opacity-10"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Title Section - Left Side */}
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <p className="text-sm uppercase tracking-wider text-white mb-4">{t('noa_platform')}</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8 text-white">
              {t('what_we_offer')}<span className="text-blue-600">.</span>
            </h2>
            
            <div className="w-16 h-1 bg-green-500 mb-8"></div>
            
            <p className="text-lg text-gray-300 mb-8 pr-6">
              {t('services_suite')}
            </p>
          </div>
          
          {/* Info Bars - Right Side */}
          <div className="lg:col-span-8 space-y-6 mt-12 lg:mt-0">
            {offerings.map((offering, index) => (
              <div 
                key={index} 
                className={cn(
                  "relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 transition-all duration-300",
                  "hover:bg-white/10 hover:border-white/20 hover:transform hover:translate-x-2"
                )}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="flex items-start gap-6">
                  <div className={cn(
                    "p-4 rounded-lg bg-white/5 border border-white/10 flex-shrink-0",
                    hoveredIndex === index ? "border-green-500/50" : ""
                  )}>
                    {React.cloneElement(offering.icon, { 
                      className: cn(
                        "w-6 h-6",
                        index === 0 ? "text-green-400" : "",
                        index === 1 ? "text-purple-400" : "",
                        index === 2 ? "text-blue-400" : "",
                        index === 3 ? "text-amber-400" : ""
                      )
                    })}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className={cn(
                      "text-xl font-space-grotesk font-bold text-white mb-3 transition-colors",
                      hoveredIndex === index && index === 0 ? "text-green-400" : "",
                      hoveredIndex === index && index === 1 ? "text-purple-400" : "",
                      hoveredIndex === index && index === 2 ? "text-blue-400" : "",
                      hoveredIndex === index && index === 3 ? "text-amber-400" : ""
                    )}>
                      {offering.title}
                    </h3>
                    
                    <p className="text-gray-300">
                      {offering.description}
                    </p>
                  </div>
                </div>
                
                {/* Accent line */}
                <div className={cn(
                  "absolute bottom-0 left-0 h-1 bg-gradient-to-r transition-all duration-500 rounded-b-lg",
                  offering.color,
                  hoveredIndex === index ? "w-full" : "w-0"
                )}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeOfferSection;
